import React from "react";
import { useDispatch } from "react-redux";
import {
    setBody,
    setBrand,
    setCountry,
    setDrive,
    setEngineType,
    setEngineVolumeFrom,
    setEngineVolumeTo,
    setGeneration,
    setHideNoImages,
    setHorsePowerFrom,
    setHorsePowerTo,
    setMileageFrom,
    setMileageTo,
    setModel,
    setOwnersQntFrom,
    setOwnersQntTo,
    setPriceFrom,
    setPriceTo,
    setSeller,
    setSource,
    setTransmission,
    setWheel,
    setYearFrom,
    setYearTo
} from "@app/store/filtersSlice";
import { Generation } from "@app/store/types";
import { SelectValue } from "../types";

export const useFilterHandlers = (generationsOptions?: Generation[]) => {
    const dispatch = useDispatch();

    const handlers = React.useMemo(
        () => ({
            handleBrandChange: (selectedOption: SelectValue | null) => {
                const brand = selectedOption
                    ? {
                          id: selectedOption.value.toString(),
                          name: selectedOption.label,
                          alias: selectedOption?.alias
                      }
                    : undefined;
                dispatch(setBrand(brand));
            },

            handleModelChange: (selectedOption: SelectValue | null) => {
                const model = selectedOption
                    ? {
                          id: selectedOption.value.toString(),
                          name: selectedOption.label,
                          alias: selectedOption.alias
                      }
                    : undefined;
                dispatch(setModel(model));
            },

            handleGenerationChange: (selectedOption: SelectValue | null) => {
                const selectedGen = generationsOptions?.find(
                    (gen: Generation) => gen.id === selectedOption?.value.toString()
                );
                dispatch(setGeneration(selectedGen || null));
            },

            handleYearFromChange: (value: SelectValue | null) => {
                dispatch(setYearFrom(value?.value as number));
            },

            handleYearToChange: (value: SelectValue | null) => {
                dispatch(setYearTo(value?.value as number));
            },

            handleMileageFromChange: (value: number | undefined) => {
                dispatch(setMileageFrom(value));
            },

            handleMileageToChange: (value: number | undefined) => {
                dispatch(setMileageTo(value));
            },

            handlePriceFromChange: (value: number | undefined) => {
                dispatch(setPriceFrom(value));
            },

            handlePriceToChange: (value: number | undefined) => {
                dispatch(setPriceTo(value));
            },

            handleSourceChange: (selectedOptions: SelectValue[] | null) => {
                const selectedIds = selectedOptions
                    ? selectedOptions.map((option: SelectValue) => option.value as number)
                    : [];
                dispatch(setSource(selectedIds));
            },

            handleCountryChange: (selectedOption: SelectValue | null) => {
                dispatch(setCountry(selectedOption?.value as string));
            },

            handleBodyChange: (value: SelectValue | null) => {
                dispatch(setBody(value?.value as string));
            },

            handleTransmissionChange: (value: SelectValue | null) => {
                dispatch(setTransmission(value?.value as string));
            },

            handleEngineTypeChange: (value: SelectValue | null) => {
                dispatch(setEngineType(value?.value as string));
            },

            handleDriveChange: (value: SelectValue | null) => {
                dispatch(setDrive(value?.value as string));
            },

            handleEngineVolumeFromChange: (value: SelectValue | null) => {
                dispatch(setEngineVolumeFrom(value?.value as number));
            },

            handleEngineVolumeToChange: (value: SelectValue | null) => {
                dispatch(setEngineVolumeTo(value?.value as number));
            },

            handleHorsePowerFromChange: (value: string) => {
                dispatch(setHorsePowerFrom(value));
            },

            handleHorsePowerToChange: (value: string) => {
                dispatch(setHorsePowerTo(value));
            },

            handleWheelChange: (value: SelectValue | null) => {
                dispatch(setWheel(value?.value as string));
            },

            handleOwnersQntFromChange: (value: SelectValue | null) => {
                dispatch(setOwnersQntFrom(value?.value as number));
            },

            handleOwnersQntToChange: (value: SelectValue | null) => {
                dispatch(setOwnersQntTo(value?.value as number));
            },

            handleSellerChange: (value: SelectValue | null) => {
                dispatch(setSeller(value?.value as string));
            },

            handleHideNoImagesChange: (hideNoImages: boolean) => {
                dispatch(setHideNoImages(!hideNoImages));
            }
        }),
        [dispatch, generationsOptions]
    );

    return handlers;
};